import { cloneElement } from "react";
import { useTranslation } from "react-i18next";
import { Edit2 } from "iconsax-react";
import { Badge, Button } from "@/shared/ui";
import type { Project } from "../../../types";
import { PROJECT_STATUS_MAP, CATEGORY_MAP } from "../../../utils";

interface Props {
  project: Project;
  onClick: (id: number) => void;
  onEdit: (project: Project) => void;
  canEdit: boolean;
}

export default function ProjectGridCard({ project, onClick, onEdit, canEdit }: Props) {
  const { t } = useTranslation("ProjectsPage");

  const statusInfo = PROJECT_STATUS_MAP[project.status];
  const categoryLabel =
    project.category && CATEGORY_MAP[project.category] ? t(CATEGORY_MAP[project.category].label) : "";

  function handleEditClick(e: React.MouseEvent) {
    e.stopPropagation();
    onEdit(project);
  }

  return (
    <div
      className="relative flex flex-col radius-lg border surface-base-stroke surface-base-fill cursor-pointer hover:shadow-sm overflow-hidden"
      onClick={() => onClick(project.id)}>
      {canEdit && (
        <div className="absolute top-3 right-3 z-100">
          <Button
            type="button"
            isIconButton
            variant="secondary"
            className="w-8! h-8! p-0! flex items-center justify-center rounded-lg! cursor-pointer content-base-secondary hover:content-base-primary surface-base-fill button-secondary-normal"
            onClick={handleEditClick}>
            <Edit2 size={16} color="currentColor" />
          </Button>
        </div>
      )}

      <div className="relative w-full h-48">
        {project.project_picture_url ? (
          <img src={project.project_picture_url} alt={project.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100" />
        )}
        <div className="absolute top-3 left-3 right-12 flex flex-wrap gap-2">
          <Badge variant="filled" color={statusInfo.color} text={t(statusInfo.label)} dot />
          {categoryLabel && (
            <Badge
              variant="soft"
              color="info"
              text={categoryLabel}
              icon={project.category && CATEGORY_MAP[project.category]?.icon
                ? cloneElement(CATEGORY_MAP[project.category].icon as React.ReactElement<{ size: number }>, { size: 14 })
                : undefined}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col flex-1">
        <div className="p-4 flex-1">
          <h3 className="text-heading-md content-base-primary">{project.name}</h3>
        </div>
      </div>
    </div>
  );
}
